import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiDownload, FiEye, FiCheckCircle, FiDollarSign, FiLoader, FiTruck, FiBox } from 'react-icons/fi';
import { ClaimDrawer } from '../components/claims/ClaimDrawer';
import supabase from '../supabase/supabase';

export const SoldClaims = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Drawer State
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    fetchSoldClaims();
  }, []);

  const fetchSoldClaims = async () => {
    setLoading(true);
    try {
      // 1. Fetch only paid claims
      const { data: claimsData, error: claimsError } = await supabase
        .from('claims_1783447171616')
        .select('*')
        .eq('payment_status', 'Paid')
        .order('created_at', { ascending: false });

      if (claimsError) throw claimsError;

      // 2. Fetch salvager profiles to map the buyer's company name
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles_1783455000000')
        .select('id, company_name');

      if (profilesError) throw profilesError;

      const profileMap = {};
      profilesData.forEach(p => {
        profileMap[p.id] = p.company_name;
      });

      // 3. Map the names to the claims
      const enriched = claimsData.map(c => ({
        ...c,
        salvagerName: profileMap[c.assigned_salvager_id] || 'Unknown Salvager'
      }));

      setClaims(enriched);
    } catch (err) {
      console.error("Error fetching sold claims:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClaim = (claim) => {
    setSelectedClaim(claim);
    setIsDrawerOpen(true);
  };

  const filteredClaims = claims.filter(c => 
    c.claim_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.make && c.make.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (c.model && c.model.toLowerCase().includes(searchQuery.toLowerCase())) ||
    c.salvagerName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalValue = claims.reduce((acc, curr) => acc + parseFloat(curr.offer_price || curr.estimated_value || 0), 0);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Sold & Finalized</h1>
          <p className="text-gray-500">Historical archive of all successfully paid and completed salvage claims.</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium text-sm transition-colors shadow-sm">
          <SafeIcon icon={FiDownload} /> Export Report
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="flex items-center gap-4 border-l-4 border-green-500">
          <div className="w-12 h-12 rounded-2xl bg-green-50 text-green-600 flex items-center justify-center">
            <SafeIcon icon={FiCheckCircle} size={24} />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Sold Items</p>
            <h3 className="text-2xl font-bold text-gray-900">{claims.length}</h3>
          </div>
        </Card>
        
        <Card className="flex items-center gap-4 border-l-4 border-brand">
          <div className="w-12 h-12 rounded-2xl bg-brand/10 text-brand flex items-center justify-center">
            <SafeIcon icon={FiDollarSign} size={24} />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Recovered Value</p>
            <h3 className="text-2xl font-bold text-gray-900">
              R {totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h3>
          </div>
        </Card>
      </div>

      <Card className="!p-0 overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gray-50/50">
          <div className="max-w-md relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search by Claim #, Make, Model, or Buyer..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 text-sm"
            />
          </div>
        </div>

        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100 text-xs uppercase text-gray-400 tracking-wider">
                <th className="px-6 py-4 font-semibold">Claim Number</th>
                <th className="px-6 py-4 font-semibold">Item Details</th>
                <th className="px-6 py-4 font-semibold">Sold To (Buyer)</th>
                <th className="px-6 py-4 font-semibold">Sale Price</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                    <SafeIcon icon={FiLoader} className="animate-spin inline-block mr-2" /> Loading sold claims...
                  </td>
                </tr>
              ) : filteredClaims.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <SafeIcon icon={FiCheckCircle} size={48} className="opacity-20" />
                      <p className="italic text-sm">No sold claims found.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredClaims.map((claim) => (
                  <tr 
                    key={claim.id} 
                    className="hover:bg-gray-50/50 transition-colors cursor-pointer group"
                    onClick={() => handleViewClaim(claim)}
                  >
                    <td className="px-6 py-4">
                      <span className="font-bold text-gray-900 group-hover:text-brand transition-colors">{claim.claim_number}</span>
                      <div className="text-xs text-gray-500 mt-1">{new Date(claim.created_at).toLocaleDateString()}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-800">{claim.salvage_item || 'N/A'}</span>
                      <div className="text-gray-500 text-xs mt-0.5">{claim.make} {claim.model}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={FiTruck} className="text-gray-400" />
                        <span className="font-medium text-gray-700">{claim.salvagerName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-green-700 bg-green-50 px-2.5 py-1 rounded-lg border border-green-100">
                        R {parseFloat(claim.offer_price || claim.estimated_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Badge status="Completed">Paid & Closed</Badge>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleViewClaim(claim); }}
                        className="p-2 text-gray-400 hover:text-brand hover:bg-brand-accent rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                        title="View Full Detail"
                      >
                        <SafeIcon icon={FiEye} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <ClaimDrawer 
        claim={selectedClaim} 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        onUpdate={fetchSoldClaims} 
      />
    </div>
  );
};